import {useEffect, useRef, useState, type SyntheticEvent} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {ChevronDown, CircleAlert, Clock, Lock} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  Spinner,
  Wordmark
} from '@kroll-resolver-crisp/ui-components-react';
import {useAuth} from '../context/useAuth';
import {REDIRECT_STORAGE_KEY} from '../context/AuthContext';
import {getApiErrorMessage} from '../api/client';
import {SESSION_EXPIRED_KEY} from '../lib/authKeys';

// The brand panel's gradient, verbatim from the design system's login template: four literal Crisp
// cyan stops with no token in them. A login page is always Crisp cyan - nobody has signed in, so
// there is no accent to apply, and an accent left on <html> by whoever signed out must not reach
// it - which is also why the page root restates `data-accent="cyan"` below. The ink is literal
// white for the same reason: the gradient is dark in both modes, and a token ink flips with the
// mode (4.0.0-rc.5's dark `--primary-foreground` is near-black, which hid the whole panel).
const PANEL =
  'bg-[linear-gradient(135deg,oklch(0.2_0.055_235)_0%,oklch(0.3_0.07_228)_40%,oklch(0.42_0.09_224)_70%,oklch(0.505_0.105_205)_100%)]';

// One user per role, seeded by the API's LocalDevUserSeeder when it runs under the Aspire AppHost.
// Outside that setup the first click just provisions the email as an analyst, like any other address.
const DEV_USERS = [
  {label: 'Analyst', email: 'analyst@dev.local'},
  {label: 'Analyst admin', email: 'analyst-admin@dev.local'},
  {label: 'Admin', email: 'admin@dev.local'}
];

export function LoginPage() {
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    error: authError,
    sessionExpired: authSessionExpired,
    login,
    loginWithDevAuth
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectFrom = (location.state as {from?: string} | null)?.from;
  const hasRedirected = useRef(false);
  // Set by the API client when a request loses its session mid-use; read once, then cleared.
  const [wasRedirectedExpired] = useState(() => {
    const flag = sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return flag;
  });
  const sessionExpired = authSessionExpired || wasRedirectedExpired;

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated || hasRedirected.current) {
      return;
    }

    hasRedirected.current = true;
    const target = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
    sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
    void navigate(target ?? '/', {replace: true});
  }, [isAuthenticated, isAuthLoading, navigate]);
  useEffect(() => {
    if (redirectFrom) {
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, redirectFrom);
    }
  }, [redirectFrom]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [devEmail, setDevEmail] = useState('');

  useEffect(() => {
    if (authError) {
      setIsLoading(false);
    }
  }, [authError]);

  const displayError = error || authError?.message;

  const handleAuth0 = () => {
    setIsLoading(true);
    setError('');
    login().catch(() => {
      setIsLoading(false);
      setError('Sign-in failed. Make sure your account is authorized.');
    });
  };

  const signInAsDev = (email: string) => {
    setIsLoading(true);
    setError('');
    loginWithDevAuth(email)
      .catch((error_: unknown) => {
        setError(getApiErrorMessage(error_, 'Dev login failed.'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDevLogin = (event: SyntheticEvent) => {
    event.preventDefault();
    if (devEmail.trim() === '') {
      return;
    }

    signInAsDev(devEmail.trim());
  };

  return (
    <div data-accent="cyan" className="bg-card text-foreground flex min-h-svh">
      <aside
        className={`hidden shrink-0 basis-[42%] flex-col justify-between p-12 text-white lg:flex ${PANEL}`}>
        <Wordmark variant="kroll" tone="white" className="h-auto w-[200px]" />
        <div className="flex max-w-[44ch] flex-col gap-4">
          <h1 className="font-heading text-[clamp(28px,3.4vw,44px)] leading-[1.05] font-medium tracking-[-0.02em]">
            Platform Risk Intelligence
          </h1>
          <p className="text-sm leading-relaxed text-pretty opacity-90">
            Risk submissions and investigations across abuse verticals — search, classify, and
            deliver to client hoppers.
          </p>
        </div>
        <div aria-hidden="true" className="h-4" />
      </aside>

      <main className="flex flex-1 items-center justify-center p-[clamp(24px,5vw,64px)]">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <h2 className="font-heading text-xl font-medium tracking-[-0.01em]">Sign in</h2>
            <p className="text-muted-foreground text-sm">
              Use your organization account to continue.
            </p>
          </div>

          {displayError ? (
            <Alert variant="destructive">
              <CircleAlert />
              <AlertTitle>We couldn&apos;t sign you in</AlertTitle>
              <AlertDescription>{displayError}</AlertDescription>
            </Alert>
          ) : null}

          {/* Session-expired notice: informational, not an error */}
          {sessionExpired && !displayError ? (
            <Alert role="status">
              <Clock />
              <AlertTitle>Your session has expired. Please sign in again.</AlertTitle>
            </Alert>
          ) : null}

          <Button className="w-full" disabled={isLoading} onClick={handleAuth0}>
            {isLoading ? <Spinner /> : <Lock />}
            {isLoading ? 'Authenticating…' : 'Log in with Auth0'}
          </Button>

          {import.meta.env.DEV ? (
            // Most dev environments sign in through this rather than Auth0, so the trigger is a full
            // button beside the SSO one rather than a muted text link.
            <Collapsible>
              <CollapsibleTrigger render={<Button variant="outline" className="w-full" />}>
                Dev environment bypass
                <ChevronDown className="transition-transform in-data-panel-open:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="flex flex-wrap gap-2 pt-3">
                  {DEV_USERS.map((user) => (
                    <Button
                      key={user.email}
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isLoading}
                      onClick={() => {
                        signInAsDev(user.email);
                      }}>
                      {user.label}
                    </Button>
                  ))}
                </div>
                <form className="pt-3" onSubmit={handleDevLogin}>
                  <FieldGroup>
                    <Field>
                      <FieldLabel htmlFor="dev-email">Email</FieldLabel>
                      <Input
                        id="dev-email"
                        type="email"
                        value={devEmail}
                        placeholder="analyst@resolver.com"
                        onChange={(event) => {
                          setDevEmail(event.target.value);
                        }}
                      />
                      <FieldDescription>Only available in local builds.</FieldDescription>
                    </Field>
                    <Button
                      type="submit"
                      variant="secondary"
                      disabled={isLoading || !devEmail.trim()}>
                      Sign in as dev
                    </Button>
                  </FieldGroup>
                </form>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </div>
      </main>
    </div>
  );
}
